import {
  ClockCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  StopOutlined,
  UploadOutlined
} from "@ant-design/icons";
import { useMsal } from "@azure/msal-react";
import {
  Button,
  Input,
  message,
  Modal,
  notification,
  Select,
  Space,
  Spin,
  Upload,
} from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";

import axiosInstance from "../../../core/index-core";
import tokenManager from "../../../core/TokenManager";
import { metrics } from "../../../metrics/client";
import { useTransportQuality } from "../../../hooks/useTransportQuality";
import { useWakeLock } from "../../../hooks/useWakeLock";
import RecordingConsentModal from "./RecordingConsentModal";
import { ERROR_CODES, ERROR_MESSAGES } from "./constants";
import { useAiNotePolling } from "../../../hooks/useAiNotePolling";
const { TextArea } = Input;

export function decodeBase64(encodedStr) {
  // Handle null/undefined
  if (!encodedStr) {
    return "";
  }

  // If it's an object, stringify it
  if (typeof encodedStr === 'object') {
    return JSON.stringify(encodedStr);
  }

  // Convert to string and trim
  const str = String(encodedStr).trim();

  if (!str) {
    return "";
  }

  // Try to decode as Base64
  try {
    const binaryStr = atob(str);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }
    return new TextDecoder("utf-8").decode(bytes);
  } catch (error) {
    // Not valid Base64 - return as-is (already plain text)
    return str;
  }
}


function transcriptToSingleLine(input) {
  return input
    .split("\n")
    .map((line) => line.replace(/^spk_\d+ \[\d{2}:\d{2}.*?\]:\s*/, ""))
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

const SessionRightPanel = ({
  timer,
  setTimer,
  meetingStatus,
  notes,
  onNotesChange,
  isNoteSigned,
  setIsNoteSigned,
  setFetchedTranscript,
  setTranscriptReceived,
  transcriptReceived,
  setLoadingMappings,
  setFieldMappings,
  setTemplate,
  setNoteJSON,
  template,
  session_id,
  patient_id,
  setRecordingTranscript,
  setLivePreview,
  hasConsent,
  onConsentGiven,
  onConsentClear,
  noteCommentSession_id,
  setFetchingNote,
  setPollingNote,
  onNoteLocked,
  recordingParent,
  setRecordingParent,
  parentRecording,
  setUploading,
  connectionState: parentConnectionState,
  wsState: parentWsState,
  setWsState: setParentWsState,
  isOnline: parentIsOnline,
  isEhrSubmitted: parentIsEhrSubmitted,
  setIsEhrSubmitted: setParentIsEhrSubmitted,
  reconnectAttempt: parentReconnectAttempt,
  setReconnectAttempt: setParentReconnectAttempt,
  isValidAppointment,
  generatingNotes,
  pollingNote,
}) => {
  const [selectedPerson, setSelectedPerson] = useState("therapist");
  const [selectedRole, setSelectedRole] = useState("follow_up");
  const [open, setOpen] = useState(false);
  const [consentModalOpen, setConsentModalOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const pendingResolveRef = useRef(null);

  const [noteLoading, setNoteLoading] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const autosaveRef = useRef(null);
  const [aiNoteStatus, setAiNoteStatus] = useState("idle");
  notification.config({ placement: "topRight" });

  const handleNoteReady = useCallback((noteData) => {
    setAiNoteStatus("ready");
    setNoteJSON?.(noteData);
    setIsNoteSigned(noteData.signed);
    if (noteData.ehr_submitted && setParentIsEhrSubmitted) {
      setParentIsEhrSubmitted(true);
    }
    setPollingNote(false);
    notification.success({ message: "AI note is ready." });
  }, [setNoteJSON, setPollingNote, setIsNoteSigned, setParentIsEhrSubmitted]);

  const handleGenerationFailed = useCallback((statusData) => {
    const errCode = statusData?.error_code;
    const errMsg = statusData?.error || "AI note generation failed.";
    if (errCode === ERROR_CODES.NOTE_LOCKED_EHR_SUBMITTED || errCode === ERROR_CODES.NOTE_LOCKED_SIGNED) {
      onNoteLocked?.();
      notification.error(ERROR_MESSAGES.LOCK_ERROR);
    } else {
      notification.error({
        message: "AI Note generation failed",
        description: errMsg,
      });
    }
    setPollingNote(false);
  }, [setPollingNote, onNoteLocked]);

  const handleGenerating = useCallback(() => {
    setAiNoteStatus("processing");
  }, []);

  const { startPolling, stopPolling, fetchNote } = useAiNotePolling({
    patientId: patient_id,
    sessionId: session_id,
    onNoteReady: handleNoteReady,
    onFailed: handleGenerationFailed,
    onGenerating: handleGenerating,
  });



  const refreshAiNote = useCallback(() => {
    setPollingNote(true);
    startPolling();
  }, [startPolling, setPollingNote]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  useEffect(() => {
    if (patient_id && session_id) {
      const checkAndPoll = async () => {
        setFetchingNote?.(true);
        let result;
        try {
          result = await fetchNote();
        } finally {
          setFetchingNote?.(false);
        }

        if (result.failed) {
          // Recent generation failure surfaced by backend — show persistent error
          setAiNoteStatus("failed");
          notification.error({
            key: "ai-note-generation-failed",
            message: "AI Note generation failed",
            description: result.error || "Generation failed. Please try again.",
            duration: 0,
          });
          return;
        }

        if (result.status === 200 && result.noteData) {
          // Note is ready — apply it
          setAiNoteStatus("ready");
          setNoteJSON?.(result.noteData);
          setIsNoteSigned(result.noteData.signed);
          if (result.noteData.ehr_submitted && setParentIsEhrSubmitted) {
            setParentIsEhrSubmitted(true);
          }
          return;
        }

        if (result.generationId) {
          // Generation in-flight — start polling with generation_id
          console.log('[SessionRightPanel] AI note still processing on mount — starting auto-poll');
          setAiNoteStatus("processing");
          setPollingNote(true);
          notification.info({
            key: 'ai-note-generation-progress',
            message: 'AI Note Generation In Progress',
            description: 'AI note generation is still in progress. You can wait here or navigate away and come back later.',
            duration: 10,
          });
          startPolling({ generationId: result.generationId });
        }
      };
      checkAndPoll();
    }
  }, [patient_id, session_id, fetchNote, startPolling, setFetchingNote, setPollingNote, setNoteJSON, setIsNoteSigned, setParentIsEhrSubmitted]);

  const requestConsent = useCallback(() => {
    if (hasConsent) return Promise.resolve(true);
    setConsentModalOpen(true);
    return new Promise((resolve) => {
      pendingResolveRef.current = resolve;
    });
  }, [hasConsent]);

  const handleConsentOk = async () => {
    if (!consentChecked) return;
    onConsentGiven?.();
    setConsentModalOpen(false);
    setConsentChecked(false);
    pendingResolveRef.current?.(true);
    pendingResolveRef.current = null;
    // Small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 300));
  };

  const handleConsentCancel = () => {
    setConsentModalOpen(false);
    setConsentChecked(false);
    pendingResolveRef.current?.(false);
    pendingResolveRef.current = null;
  };

  const nudgeGenerateButton = useCallback((reason) => {
    try {
      window.dispatchEvent(new CustomEvent('ai-notes:nudge', { detail: { reason } }));
    } catch (err) {
      console.warn('[nudgeGenerateButton] Failed to dispatch event:', err);
    }
  }, []);

  useEffect(() => {
    if (selectedPerson == "therapist" && selectedRole == "follow_up")
      setTemplate("Therapist Progress Note 2022");
    if (selectedPerson == "therapist" && selectedRole == "initial_eval")
      setTemplate("Therapist Initial Evaluation 2022");
    if (selectedPerson == "prescriber" && selectedRole == "follow_up")
      setTemplate("Follow-up 4");
    if (selectedPerson == "prescriber" && selectedRole == "initial_eval")
      setTemplate("Initial Evaluation 4");
  }, [selectedPerson, selectedRole]);

  const handleStopped = async () => {
    try {
      setLoadingMappings(true);

      notification.success({
        message: "Please click on Generate AI Notes",
      });
    } catch (err) {
      notification.error({
        message: "Couldn't show next steps",
        description: "Please try again.",
      });
    } finally {
      setLoadingMappings(false);
      if (setUploading) setUploading(false);
    }
  };

  return (
    <div className="w-[373px] flex flex-col gap-6" style={{ flexShrink: 0 }}>
      <div
        className="bg-white rounded-xl border border-gray-100 p-5 flex flex-col gap-8"
        style={{
          minHeight: "142px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
        }}
      >
        <div className="flex items-center justify-center">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
              <ClockCircleOutlined
                style={{
                  fontSize: "16px",
                  color: "#1677ff",
                }}
              />
            </div>
            <div className="text-lg font-semibold text-gray-800 font-mono tracking-wide">
              {timer}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          {meetingStatus === "not-started" && (
            <Button
              type="primary"
              size="large"
              block
              style={{
                height: "44px",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              Start Meeting
            </Button>
          )}

          {meetingStatus === "active" && (
            <Button
              type="primary"
              danger
              size="large"
              block
              style={{
                height: "44px",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              End Meeting
            </Button>
          )}

          {meetingStatus === "ended" && (
            <div className="flex justify-center">
              <ControlButtons
                session_id={session_id}
                isNoteSigned={isNoteSigned}
                isEhrSubmitted={parentIsEhrSubmitted}
                parentRecording={parentRecording}
                patient_id={patient_id}
                setRecordingTranscript={setRecordingTranscript}
                setLivePreview={setLivePreview}
                setTimer={setTimer}
                setFetchedTranscript={setFetchedTranscript}
                setTranscriptReceived={setTranscriptReceived}
                setLoadingMappings={setLoadingMappings}
                requestConsent={requestConsent}
                onStopped={handleStopped}
                setRecordingParent={setRecordingParent}
                setUploading={setUploading}
                nudgeGenerateButton={nudgeGenerateButton}
                connectionState={parentConnectionState}
                wsState={parentWsState}
                setWsState={setParentWsState}
                isOnline={parentIsOnline}
                reconnectAttempt={parentReconnectAttempt}
                setReconnectAttempt={setParentReconnectAttempt}
                isValidAppointment={isValidAppointment}
                generatingNotes={generatingNotes}
                pollingNote={pollingNote}
              />
            </div>
          )}
        </div>
      </div>

      {/* Settings Modal */}
      <Modal
        open={open}
        title={
          <span
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#1f2937",
            }}
          >
            Select Clinician Settings
          </span>
        }
        onCancel={() => setOpen(false)}
        centered
        footer={null}
        destroyOnClose
        styles={{
          content: {
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <div className="flex flex-col gap-4 pt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Clinician Type
            </label>
            <Select
              value={selectedPerson}
              style={{ width: "100%", height: "40px" }}
              onChange={(e) => setSelectedPerson(e)}
              options={[
                { value: "prescriber", label: "Prescriber" },
                { value: "therapist", label: "Therapist" },
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Session Type
            </label>
            <Select
              value={selectedRole}
              style={{ width: "100%", height: "40px" }}
              onChange={(e) => setSelectedRole(e)}
              options={[
                { value: "initial_eval", label: "Initial Evaluation" },
                { value: "follow_up", label: "Follow Up" },
              ]}
            />
          </div>
        </div>
      </Modal>

      <RecordingConsentModal
        transcriptExists={transcriptReceived}
        open={consentModalOpen}
        checked={consentChecked}
        onCheck={setConsentChecked}
        onOk={handleConsentOk}
        onCancel={handleConsentCancel}
      />
    </div>
  );
};

const ControlButtons = React.memo(
  ({
    session_id,
    patient_id,
    setRecordingTranscript,
    setLivePreview,
    setTimer,
    requestConsent,
    onStopped,
    setRecordingParent,
    parentRecording,
    setFetchedTranscript,
    setTranscriptReceived,
    setLoadingMappings,
    setUploading,
    nudgeGenerateButton,
    isNoteSigned,
    isEhrSubmitted,
    connectionState: parentConnectionState,
    wsState: parentWsState,
    setWsState: setParentWsState,
    isOnline: parentIsOnline,
    reconnectAttempt: parentReconnectAttempt,
    setReconnectAttempt: setParentReconnectAttempt,
    isValidAppointment,
    generatingNotes,
    pollingNote,
  }) => {
    // Upload-related state (moved from parent)
    const [uploading, setUploadingLocal] = useState(false);
    const [jsonContent, setJsonContent] = useState(null);
    const [isTxt, setIsTxt] = useState(false);

    // Upload handler (moved from parent)
    const handleUpload = useCallback(async (jsonContent) => {
      setLoadingMappings(true);
      setTranscriptReceived(false);
      setFetchedTranscript(null);

      try {
        notification.info({
          message: "Upload in progress",
          description: "Please wait for the upload to be completed",
        });
        setUploadingLocal(true);
        if (setUploading) setUploading(true);

        await axiosInstance.put(
          `patient/${patient_id}/sessions/${session_id}/transcription`,
          isTxt ? { content: jsonContent } : { ...jsonContent }
        );

        const data2 = await axiosInstance.get(
          `patient/${patient_id}/sessions/${session_id}/transcription`
        );

        setTimeout(() => {
          setUploadingLocal(false);
          if (setUploading) setUploading(false);
        }, 3000);

        // Validate that content exists
        if (!data2?.data?.content) {
          notification.error({
            message: "Upload succeeded but transcript content is empty",
            description: "Please try uploading again or contact support.",
          });
          return;
        }

        const decodedContent = decodeBase64(data2?.data?.content);

        if (!decodedContent || decodedContent.length === 0) {
          notification.error({
            message: "Transcript content is empty after decoding",
            description: "Please try uploading again or check the file format.",
          });
          return;
        }

        notification.success({
          message: "Please click on Generate AI Notes",
        });

        setFetchedTranscript(decodedContent);
        setTranscriptReceived(true);
        nudgeGenerateButton('uploaded');
      } catch (err) {
        notification.error({
          message: "The transcription could not be uploaded.",
          description: err?.response?.data?.detail || "Please try after some time.",
        });
      } finally {
        setLoadingMappings(false);
        if (setUploading) setUploading(false);
        // Reset jsonContent to allow re-uploading the same file
        setJsonContent(null);
      }
    }, [patient_id, session_id, isTxt, setLoadingMappings, setTranscriptReceived, setFetchedTranscript, setUploading, nudgeGenerateButton]);

    // beforeUpload handler (moved from parent)
    const beforeUpload = useCallback((file) => {
      const isJson =
        file.type === "application/json" || file.name.endsWith(".json");
      const isTxtFile = file.type === "text/plain" || file.name.endsWith(".txt");

      if (!isJson && !isTxtFile) {
        notification.error({ message: "You can only upload .json and .txt files!" });
        return false;
      }

      if (file.size === 0) {
        notification.error({ message: "Uploaded file is empty!" });
        return false;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;

        if (!content || content.trim().length === 0) {
          notification.error({ message: "Uploaded file is empty" });
          return;
        }

        if (isJson) {
          try {
            const parsed = JSON.parse(content);
            setJsonContent(parsed);
            setIsTxt(false);
          } catch (err) {
            notification.error({ message: "Invalid JSON file!" });
          }
        } else if (isTxtFile) {
          setIsTxt(true);
          setJsonContent(content);
        }
      };

      reader.onerror = () => {
        notification.error({ message: "Failed to read file." });
      };

      reader.readAsText(file);
      return false;
    }, []);

    // Trigger upload when jsonContent changes
    useEffect(() => {
      if (jsonContent) {
        handleUpload(jsonContent);
      }
    }, [jsonContent, handleUpload]);

    const buttonStyle = {
      width: 32,
      height: 32,
      borderRadius: "50%",
      padding: 0,
      fontSize: 14,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
    };

    const getWebSocketUrl = () => {
      if (process.env.REACT_APP_MICROSERVICE_HOST_PRIME) {
        const host = process.env.REACT_APP_MICROSERVICE_HOST_PRIME.replace(/^https?:\/\//, "");
        // Use ws:// for localhost, wss:// for other hosts
        const protocol = host.includes("localhost") ? "ws" : "wss";
        return `${protocol}://${host}/patient/${patient_id}/sessions/${session_id}/recording`;
      }
      // Use current host for automatic environment detection
      const host = window.location.host;
      const protocol = window.location.protocol === "https:" ? "wss" : "ws";
      return `${protocol}://${host}/fastapi/patient/${patient_id}/sessions/${session_id}/recording`;
    };

    const WS_URL = getWebSocketUrl();

    const wsRef = useRef(null);
    const processorRef = useRef(null);
    const audioContextRef = useRef(null);
    const sourceRef = useRef(null);
    const streamRef = useRef(null);

    const intervalRef = useRef(null);
    const startRef = useRef(null);
    const elapsedRef = useRef(0);

    const fmt = (ms) => {
      const totalSec = Math.floor(ms / 1000);
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(h)}:${pad(m)}:${pad(s)}`;
    };
    const pushTimer = (ms) =>
      typeof setTimer === "function" && setTimer(fmt(ms));

    const startTicking = () => {
      startRef.current = Date.now() - elapsedRef.current;
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        const ms = Date.now() - startRef.current;
        elapsedRef.current = ms;
        pushTimer(ms);
      }, 250);
    };

    const stopTicking = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const [recording, setRecording] = useState(false);
    const [paused, setPaused] = useState(false);

    // ============================================================================
    // OS SLEEP/LOCK PREVENTION: Wake Lock + Silent Video Keepalive
    // Prevents the OS from going to sleep or locking the screen during recording.
    // Works in both active tab and background tab scenarios.
    // - Layer 1: Screen Wake Lock API (active tab)
    // - Layer 2: Silent video keepalive (background tab)
    // ============================================================================
    useWakeLock(recording && !paused);

    const transportQuality = useTransportQuality({
      wsRef,
      recording,
      sessionId: session_id,
      patientId: patient_id,
      metrics,
    });

    // Use parent's states
    const connectionState = parentConnectionState || 'disconnected';
    const wsState = parentWsState || 'idle';
    const setWsState = setParentWsState || (() => { });
    const isOnline = parentIsOnline !== undefined ? parentIsOnline : true;
    const reconnectAttempts = parentReconnectAttempt || 0;
    const setReconnectAttempts = setParentReconnectAttempt || (() => { });
    // FIX (Stale Closure): Use a ref for reconnect attempt count so that
    // attemptReconnect and handleWebSocketClose always read the latest value,
    // even when captured in WebSocket event handler closures.
    // The parent state (reconnectAttempts / setReconnectAttempts) is still
    // updated for UI display (ConnectionStatus banner shows "1/5", "2/5", etc.)
    const reconnectAttemptsRef = useRef(0);
    // FIX (Stale Closure Audit — Circular Dep): Ref to hold the latest handleWebSocketClose
    // so that attemptReconnect can assign it to reconnected WebSockets without capturing
    // a stale version. This breaks the circular useCallback dependency between
    // attemptReconnect ↔ handleWebSocketClose.
    const handleWebSocketCloseRef = useRef(null);
    const { instance, accounts } = useMsal();
    const pausedRef = useRef(false);
    const recordingRef = useRef(false); // EC-22 FIX: Ref to avoid stale closure in handleWebSocketClose
    const pausedByDisconnectRef = useRef(false); // Track if pause was caused by disconnection
    const reconnectTimeoutRef = useRef(null);
    const intentionalCloseRef = useRef(false); // Track if close was intentional (user clicked stop)
    const wakeLockReleaseRef = useRef(null); // Web Lock API release function (prevents background throttling)
    const audioContextWatchdogRef = useRef(null); // Periodic AudioContext suspension check
    const wsConnectionTimeoutRef = useRef(null); // Timeout for initial WebSocket connection
    const lastAudioChunkSentRef = useRef(null); // Timestamp of last audio chunk sent (for gap detection)
    const videoPlayListenerRef = useRef(null); // Document-level video play listener (AudioContext resume)
    // NOTE: wsConnectionFailed is local state in ControlButtons but is also communicated to
    // Session.js via the 'recording:wsConnectionFailed' custom event. This dual-state pattern
    // exists because ControlButtons is a React.memo child and cannot directly set parent state.
    // The local state is kept for potential future local UI use within ControlButtons.
    const [wsConnectionFailed, setWsConnectionFailed] = useState(false); // Track if WebSocket connection permanently failed during recording

    // Reconnection constants
    const MAX_RECONNECT_ATTEMPTS = 5;
    const RECONNECT_BASE_DELAY = 2000; // 2 seconds
    const HEARTBEAT_INTERVAL = 15000; // 15 seconds
    const HEARTBEAT_TIMEOUT = 10000; // 10 seconds
    const TRANSCRIPT_INACTIVITY_TIMEOUT = 20000; // 20 seconds
    // Circuit breaker: detect rapid reconnection cascades
    const RAPID_RECONNECT_WINDOW = 60000; // 60 seconds
    const RAPID_RECONNECT_THRESHOLD = 3;
    const reconnectTimestampsRef = useRef([]);

    const livePreviewRef = useRef(new Map());
    const lastCommittedRef = useRef(new Map());
    const sentenceBufferRef = useRef(new Map());
    const timestampOffsetRef = useRef(0); // Offset to add to timestamps when recovering from previous session
    const USE_SENTENCE_BUFFER = false;

    // First-segment validation refs
    const firstSegmentReceivedRef = useRef(false);
    const firstSegmentTimeoutRef = useRef(null);

    // Heartbeat (ping/pong) refs
    const heartbeatIntervalRef = useRef(null);
    const lastPongReceivedRef = useRef(null);
    const heartbeatTimeoutRef = useRef(null);

    // Transcript inactivity timeout refs (FIX for Issue #5)
    const lastTranscriptTimeRef = useRef(null);
    const transcriptInactivityTimeoutRef = useRef(null);
    // FIX (Silence Recovery): Track inactivity state to prevent notification spam
    // and support grace periods after silence recovery
    const transcriptInactivityCountRef = useRef(0);  // Number of consecutive inactivity notifications shown
    const transcriptInactivityActiveRef = useRef(false); // Whether we're currently in an "inactive" state
    const MAX_INACTIVITY_NOTIFICATIONS = 3; // Max recurring notifications before stopping spam
    const SILENCE_RECOVERY_GRACE_PERIOD = 45000; // 45 seconds grace period after recovering from silence

    // Helper function to start recurring transcript inactivity monitoring
    // @param {boolean} useGracePeriod - If true, uses a longer timeout (45s) to allow
    //   the backend transcription pipeline to recover after a period of silence
    const startTranscriptInactivityMonitoring = useCallback((useGracePeriod = false) => {
      // Don't start monitoring if recording is paused
      if (pausedRef.current) {
        return;
      }

      // Clear any existing timeout
      if (transcriptInactivityTimeoutRef.current) {
        clearTimeout(transcriptInactivityTimeoutRef.current);
      }

      // Update last transcript time
      lastTranscriptTimeRef.current = Date.now();

      // FIX (Silence Recovery): Use longer timeout after recovering from inactivity
      // to give the backend transcription service time to fully restart its pipeline
      const timeout = useGracePeriod ? SILENCE_RECOVERY_GRACE_PERIOD : TRANSCRIPT_INACTIVITY_TIMEOUT;

      // Set timeout that will fire after the configured period and then reset itself
      transcriptInactivityTimeoutRef.current = setTimeout(() => {
        // Double-check recording is not paused before showing notification
        if (pausedRef.current) {
          return;
        }

        transcriptInactivityCountRef.current++;
        transcriptInactivityActiveRef.current = true;

        // FIX (Silence Recovery): Only show notification up to MAX_INACTIVITY_NOTIFICATIONS times
        // to prevent notification fatigue during extended silence periods.
        // After the max, we silently continue monitoring so recovery still works.
        if (transcriptInactivityCountRef.current <= MAX_INACTIVITY_NOTIFICATIONS) {
          console.warn(`[WebSocket] No transcript received for ${timeout / 1000} seconds (notification ${transcriptInactivityCountRef.current}/${MAX_INACTIVITY_NOTIFICATIONS})`);

          // Telemetry: transcript inactivity (Gap #6)
          metrics.add(metrics.transcriptInactivityEvent({
            session_id,
            patient_id,
            inactivity_count: transcriptInactivityCountRef.current,
            timeout_seconds: Math.round(timeout / 1000),
          }));

          notification.warning({
            key: 'transcript-inactive',
            message: 'No Transcript Activity',
            description: transcriptInactivityCountRef.current === 1
              ? 'No new transcript has been received for 20 seconds. Please check your microphone or ensure you\'re speaking.'
              : `Still no transcript activity. Please check your microphone or ensure you're speaking. (${transcriptInactivityCountRef.current}/${MAX_INACTIVITY_NOTIFICATIONS})`,
            duration: 10,
          });
        } else {
          console.warn(`[WebSocket] No transcript activity (suppressing notification - count: ${transcriptInactivityCountRef.current})`);
        }

        // FIX (Silence Recovery): Apply exponential backoff to recurring checks
        // 20s → 40s → 60s → 60s (capped) to reduce unnecessary processing
        const nextTimeout = Math.min(
          TRANSCRIPT_INACTIVITY_TIMEOUT * Math.pow(2, transcriptInactivityCountRef.current - 1),
          60000 // Cap at 60 seconds
        );

        transcriptInactivityTimeoutRef.current = setTimeout(() => {
          startTranscriptInactivityMonitoring();
        }, nextTimeout - timeout); // Adjust for already elapsed time
      }, timeout);
    }, []);

    const commitLine = (speaker, text, timestamp, ets) => {
      const s = (speaker || "Speaker 1").toString();
      const t = (text || "").trim();
      if (!t) return;

      const last = (lastCommittedRef.current.get(s) || "").trim();
      if (t === last) return;
      setRecordingTranscript((prev) => [
        ...prev,
        {
          type: "complete",
          transcript: t,
          speaker: s,
          timestamp,
          end_time: ets,
          start_time: timestamp,
        },
      ]);
      lastCommittedRef.current.set(s, t);
    };

    const publishLivePreview = () => {
      if (typeof setLivePreview === "function") {
        setLivePreview(new Map(livePreviewRef.current));
      }
    };

    // Cleanup audio resources - defined early to be available in handlers
    const cleanupAudio = (stopTracks = true) => {
      try {
        if (processorRef.current) {
          try {
            processorRef.current.disconnect();
          } catch { }
        }
        if (sourceRef.current) {
          try {
            sourceRef.current.disconnect();
          } catch { }
        }
        if (audioContextRef.current) {
          try {
            audioContextRef.current.close();
          } catch { }
        }
        if (stopTracks && streamRef.current) {
          streamRef.current.getTracks().forEach((t) => {
            try {
              t.stop();
            } catch { }
          });
        }
      } finally {
        processorRef.current = null;
        sourceRef.current = null;
        audioContextRef.current = null;
        if (stopTracks) streamRef.current = null;
      }
    };

    // WebSocket message handler (extracted for reusability during reconnect)
    const handleWebSocketMessage = useCallback((event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      // Mark first segment received (any valid message from backend)
      if (!firstSegmentReceivedRef.current &&
        (data.type === 'partial' || data.type === 'complete' /* || data.type === 'recovered_draft'  */|| data.type === 'ready')) {
        firstSegmentReceivedRef.current = true;
        if (firstSegmentTimeoutRef.current) {
          clearTimeout(firstSegmentTimeoutRef.current);
          firstSegmentTimeoutRef.current = null;
        }
        console.log('[WebSocket] First segment received - connection validated');
        
        // FIX (Infinite Reconnect Loop): Reset reconnect counter only after the
        // backend has validated the connection by sending a real message. Previously
        // reset in ws.onopen (TCP connect), which fires before backend validation.
        // If the backend immediately rejects (e.g., 1008 "Session already active"),
        // the counter was already 0, causing infinite reconnection attempts that
        // never reached MAX_RECONNECT_ATTEMPTS.
        if (reconnectAttemptsRef.current > 0) {
          reconnectAttemptsRef.current = 0;
          setReconnectAttempts(0);
          console.log('[WebSocket] Reconnect counter reset — connection confirmed by backend');
        }
      }

      // Handle "ready" acknowledgment from backend
      if (data.type === "ready") {
        console.log('[WebSocket] Backend ready acknowledgment received');
        return;
      }

      // Handle pong response from backend (heartbeat)
      // FIX (RCA 1A): Also treat backend "keepalive" messages as heartbeat signals.
      // Backend sends keepalive every 30s independently of ping/pong. Treating it
      // as equivalent to pong prevents unnecessary reconnections when pong is missing
      // but the connection is actually alive.
      if (data.type === "pong" || data.type === "keepalive") {
        if (data.type === "keepalive") {
          console.log('[WebSocket] Keepalive received - treating as heartbeat signal');
        } else {
          console.log('[WebSocket] Pong received - connection healthy');
        }
        lastPongReceivedRef.current = Date.now();

        // Clear any pending heartbeat timeout
        if (heartbeatTimeoutRef.current) {
          clearTimeout(heartbeatTimeoutRef.current);
          heartbeatTimeoutRef.current = null;
        }
        return;
      }

      // COMMENTED OUT: Backend no longer sends recovered_draft via WebSocket
      // Recovery is now handled via API call in startRecording function
      // // Handle recovered draft from backend (reconnect scenario)
      // if (data.type === "recovered_draft") {
      //   notification.info({
      //     message: "Previous Recording Recovered",
      //     description: `Recovered ${data.char_count || 0} characters from your previous session.`,
      //   });

      //   // Set timestamp offset for continuing timestamps from where we left off
      //   // Backend sends timestamp_offset which is the last_timestamp from the previous recording
      //   if (data.timestamp_offset && typeof data.timestamp_offset === 'number') {
      //     timestampOffsetRef.current = data.timestamp_offset;
      //     console.log(`[Transcript] Setting timestamp offset to ${data.timestamp_offset}s for continued recording`);
      //   }

      //   // Prepend recovered content to transcript with visual separator
      //   if (data.transcript) {
      //     setRecordingTranscript((prev) => [
      //       {
      //         type: "recovered",
      //         transcript: data.transcript,
      //         timestamp: "Previous Session",
      //         isRecovered: true,
      //       },
      //       {
      //         type: "separator",
      //         transcript: "--- Continued Recording ---",
      //         timestamp: "",
      //         isSeparator: true,
      //       },
      //       ...prev,
      //     ]);

      //     // Note: We intentionally do NOT update fetchedTranscript here.
      //     // The recordingTranscript state already contains the recovered content,
      //     // and setting fetchedTranscript would cause the wrong component to render
      //     // (TranscriptView instead of TranscriptRecording).
      //   }
      //   return;
      // }

      // Handle error messages from backend
      if (data.type === "error") {
        const errorCode = data.error_code;

        // Handle auth errors (401)
        if (errorCode === ERROR_CODES.AUTH_TOKEN_MISSING ||
          errorCode === ERROR_CODES.AUTH_TOKEN_INVALID) {
          // FIX (Audit Issue I): Emit telemetry for auth errors received via WebSocket
          // messages, matching the pattern used for token acquisition failures in startRecording.
          metrics.add(metrics.authErrorEvent({
            error_type: errorCode,
            silent_or_interactive: 'websocket',
            error_message: data.message || ERROR_MESSAGES[errorCode]?.message,
          }));
          metrics.flush(); // Flush immediately — high-value domain error event

          notification.error({
            message: ERROR_MESSAGES[errorCode]?.message || "Authentication Error",
            description: ERROR_MESSAGES[errorCode]?.description || data.message,
            duration: 0,
          });
          intentionalCloseRef.current = true; // Don't try to reconnect
          cleanupAudio(true);
          setRecording(false);
          setRecordingParent(false);
          stopTicking();
          return;
        }

        // Handle duplicate recording error
        if (errorCode === ERROR_CODES.SESSION_ALREADY_RECORDING) {
          // Telemetry: session already recording (Gap #1)
          metrics.add(metrics.websocketErrorEvent({
            was_recording: true,
            session_id,
            patient_id,
            cause: 'session_already_recording',
          }));
          metrics.flush();

          notification.error({
            message: ERROR_MESSAGES.SESSION_ALREADY_RECORDING.message,
            description: ERROR_MESSAGES.SESSION_ALREADY_RECORDING.description,
            duration: 0,
          });
          intentionalCloseRef.current = true;
          cleanupAudio(true);
          setRecording(false);
          setRecordingParent(false);
          stopTicking();
          return;
        }

        // Generic error — Gap #2: emit telemetry for unknown backend WS errors
        metrics.add(metrics.websocketErrorEvent({
          was_recording: recordingRef.current,
          session_id,
          patient_id,
          cause: `backend_error: ${data.message || errorCode || 'unknown'}`,
        }));

        notification.error({
          message: "Recording Error",
          description: data.message || "An error occurred during recording.",
        });
        return;
      }

      // Handle pause/resume control responses
      if (
        data.type === "control_response" &&
        (data.action === "paused" || data.action === "resumed")
      ) {
        setRecordingTranscript((prev) => [
          ...prev,
          { type: data.action, timestamp: data.timestamp },
        ]);
        return;
      }

      // Handle transcript messages
      if (
        (data.type === "partial" || data.type === "complete") &&
        data.transcript
      ) {
        const speaker = data.speaker || "Speaker 1: ";
        // Apply timestamp offset for continued recordings (reconnect/stop-start scenarios)
        const offset = timestampOffsetRef.current || 0;
        const rawTs = data.timestamp || data.start_time;
        const rawEts = data.end_time;
        const ts = typeof rawTs === 'number' ? rawTs + offset : rawTs;
        const ets = typeof rawEts === 'number' ? rawEts + offset : rawEts;

        // FIX (Issue #5 + Silence Recovery): Reset transcript inactivity monitoring when transcript arrives
        // This stops recurring notifications and starts fresh monitoring.
        // Also dismiss any existing inactivity notification immediately so the user
        // sees that transcription has recovered, and use a grace period if we were
        // previously in an inactive state to give the backend pipeline time to stabilize.
        notification.destroy('transcript-inactive');
        const wasInactive = transcriptInactivityActiveRef.current;
        transcriptInactivityCountRef.current = 0;
        transcriptInactivityActiveRef.current = false;
        startTranscriptInactivityMonitoring(wasInactive); // Use grace period if recovering from inactivity
        transportQuality.onTranscriptReceived();

        if (data.type === "partial") {
          livePreviewRef.current.set(speaker, data.transcript);
          publishLivePreview();
          return;
        }

        if (data.type === "complete") {
          if (USE_SENTENCE_BUFFER) {
            const prevBuf = sentenceBufferRef.current.get(speaker) || "";
            const combined = (prevBuf + " " + data.transcript)
              .replace(/\s+/g, " ")
              .trim();
            const sentenceEnd = /[.?!…।]$/.test(combined);
            if (sentenceEnd) {
              commitLine(speaker, combined, ts, ets);
              sentenceBufferRef.current.set(speaker, "");
            } else {
              sentenceBufferRef.current.set(speaker, combined);
            }
          } else {
            commitLine(speaker, data.transcript, ts, ets);
          }
          livePreviewRef.current.delete(speaker);
          publishLivePreview();
        }
      }
    }, [setRecordingTranscript, setFetchedTranscript, setRecordingParent, commitLine, publishLivePreview, stopTicking, cleanupAudio, startTranscriptInactivityMonitoring, transportQuality.onTranscriptReceived]);

    // WebSocket error handler
    // NOTE: Per WebSocket spec, onerror is ALWAYS followed by onclose.
    // Telemetry is emitted only in handleWebSocketClose to avoid duplicate events.
    // This handler only logs and shows a transient user notification.
    // FIX (Stale Closure Audit — Bug #1): Use recordingRef.current instead of
    // closure-captured `recording` state. The WS handler reference is assigned once
    // at WebSocket creation time and is never re-assigned when the useCallback
    // identity changes, so the captured `recording` value would be stale.
    const handleWebSocketError = useCallback((error) => {
      console.error('[WebSocket] Error occurred:', error);

      // Notify user if recording (will be followed by onclose notification)
      if (recordingRef.current) {
        notification.warning({
          key: 'ws-error',
          message: "Connection Issue Detected",
          description: "A connection error occurred. Attempting to recover...",
          duration: 3000, // Auto-dismiss after 3s (onclose notification will replace it)
        });
      }
    }, []);

    // Attempt reconnection with exponential backoff and circuit breaker
    const attemptReconnect = useCallback(async (options = {}) => {
      const { immediate = false } = options;

      // EC-24 FIX: Clear any existing reconnect timeout to prevent overlapping attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // FIX (Stale Closure): Read from ref instead of closure-captured state
      const currentAttempts = reconnectAttemptsRef.current;

      if (currentAttempts >= MAX_RECONNECT_ATTEMPTS) {
        console.error('[WebSocket] All reconnection attempts exhausted', {
          attempts: currentAttempts,
          max: MAX_RECONNECT_ATTEMPTS,
          session_id,
          patient_id,
          elapsed_time: fmt(elapsedRef.current),
        });

        // Telemetry: all reconnection attempts exhausted — recording permanently lost
        metrics.add(metrics.websocketErrorEvent({
          was_recording: true,
          session_id,
          patient_id,
          cause: 'reconnection_exhausted',
        }));
        metrics.add(metrics.recordingInterruptedEvent({
          session_id,
          patient_id,
          duration_before_failure: Math.round(elapsedRef.current / 1000),
          cause: `All ${MAX_RECONNECT_ATTEMPTS} WebSocket reconnection attempts exhausted`,
        }));
        metrics.flush(); // Flush immediately — high-value domain error events

        // Show persistent error notification telling user to stop and restart
        notification.error({
          key: 'connection-lost',
          message: ERROR_MESSAGES.WEBSOCKET_DISCONNECTED.message,
          description: ERROR_MESSAGES.WEBSOCKET_DISCONNECTED.description,
          duration: 0, // Persistent — user must dismiss or act
        });

        // Clear all timers and intervals before stopping
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
        if (heartbeatTimeoutRef.current) {
          clearTimeout(heartbeatTimeoutRef.current);
          heartbeatTimeoutRef.current = null;
        }
        if (firstSegmentTimeoutRef.current) {
          clearTimeout(firstSegmentTimeoutRef.current);
          firstSegmentTimeoutRef.current = null;
        }
        // Clear transcript inactivity timeout
        if (transcriptInactivityTimeoutRef.current) {
          clearTimeout(transcriptInactivityTimeoutRef.current);
          transcriptInactivityTimeoutRef.current = null;
        }

        // Reset tracking
        firstSegmentReceivedRef.current = false;
        lastPongReceivedRef.current = null;

        cleanupAudio(true);
        stopTicking(); // Stop the timer — connection is dead

        // Mark WebSocket connection as permanently failed so the banner shows the error state
        setWsConnectionFailed(true);
        setRecording(false);
        recordingRef.current = false;
        setRecordingParent(false);
        setWsState('disconnected');  // Update WebSocket state

        // FIX (Exhaustion Cleanup): Reset pause state so UI doesn't show stale pause controls
        setPaused(false);
        pausedRef.current = false;
        pausedByDisconnectRef.current = false;

        // FIX (Exhaustion Cleanup): Reset reconnect counter so next startRecording starts fresh
        reconnectAttemptsRef.current = 0;
        setReconnectAttempts(0);

        // FIX (Exhaustion Cleanup): Reset transcript tracking refs
        transcriptInactivityCountRef.current = 0;
        transcriptInactivityActiveRef.current = false;
        lastTranscriptTimeRef.current = null;

        // FIX (Exhaustion Cleanup): Reset timestamp offset
        timestampOffsetRef.current = 0;

        // FIX (Exhaustion Cleanup): Release Web Lock to free browser resources
        if (wakeLockReleaseRef.current) {
          wakeLockReleaseRef.current();
          wakeLockReleaseRef.current = null;
          console.log('[WebLock] Released recording lock (reconnection exhausted)');
        }

        // FIX (Exhaustion Cleanup): Clear AudioContext watchdog interval
        if (audioContextWatchdogRef.current) {
          clearInterval(audioContextWatchdogRef.current);
          audioContextWatchdogRef.current = null;
        }

        // Dispatch event so Session.js can also react to connection failure
        try {
          window.dispatchEvent(new CustomEvent('recording:wsConnectionFailed'));
        } catch (err) {
          console.warn('[WebSocket] Failed to dispatch wsConnectionFailed event:', err);
        }
        return;
      }

      // Circuit breaker: detect rapid reconnection cascades
      const now = Date.now();
      reconnectTimestampsRef.current.push(now);
      reconnectTimestampsRef.current = reconnectTimestampsRef.current.filter(
        t => now - t < RAPID_RECONNECT_WINDOW
      );

      let delay;
      if (immediate) {
        delay = 0;
      } else if (reconnectTimestampsRef.current.length >= RAPID_RECONNECT_THRESHOLD) {
        // Rapid reconnection detected — use aggressive backoff
        delay = RECONNECT_BASE_DELAY * Math.pow(2, currentAttempts);
        console.warn(
          `[WebSocket] Rapid reconnection cascade detected ` +
          `(${reconnectTimestampsRef.current.length} in ${RAPID_RECONNECT_WINDOW / 1000}s). ` +
          `Using aggressive backoff: ${delay}ms`
        );
      } else {
        delay = RECONNECT_BASE_DELAY * Math.pow(1.5, currentAttempts);
      }

      reconnectTimeoutRef.current = setTimeout(async () => {
        // FIX (Stale Closure Audit — Bug #H): Guard against race condition where
        // user clicks Stop while the reconnect setTimeout is pending. Without this,
        // a new WebSocket could be created after recording was already stopped.
        if (!recordingRef.current) {
          console.log('[WebSocket] Reconnect aborted — recording was stopped while waiting');
          return;
        }

        // Increment ref FIRST (source of truth), then sync parent state for UI
        reconnectAttemptsRef.current = currentAttempts + 1;
        setReconnectAttempts(reconnectAttemptsRef.current);
        transportQuality.onReconnectStart();

        console.log(`[WebSocket] Reconnection attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS}`, {
          session_id,
          patient_id,
          delay_ms: delay,
        });

        try {
          // FIX (RCA 4A/4D): Use TokenManager for reconnection token acquisition.
          // TokenManager returns a proactively-refreshed token, ensuring reconnections
          // succeed even for sessions lasting 2-4+ hours. forceRefresh: true ensures
          // we get the freshest possible token for the new WebSocket connection.
          const token = await tokenManager.getToken({ forceRefresh: true });
          if (!token) {
            throw new Error('TokenManager returned no token for reconnection');
          }

          // FIX (Orphan WS): Close any existing WebSocket before creating a new one during
          // reconnection. In the network-lost → restored scenario, the old WS may still be
          // in OPEN or CONNECTING state (browser hasn't detected TCP death yet). Without
          // closing it, the old WS continues receiving messages from the backend while the
          // new WS sends audio — causing split-brain communication. Null out handlers first
          // to prevent the close event from triggering another attemptReconnect (infinite loop).
          if (wsRef.current) {
            console.warn('[WebSocket] Closing old WebSocket before reconnection attempt');
            try {
              wsRef.current.onclose = null;
              wsRef.current.onerror = null;
              wsRef.current.onmessage = null;
              wsRef.current.close(1000, "Reconnecting with new WebSocket");
            } catch { }
            wsRef.current = null;
          }

          // Create new WebSocket connection
          const ws = new WebSocket(WS_URL, [`Bearer-${token}`, "dfeeergvcdcsdf"]);
          wsRef.current = ws;

          ws.onopen = () => {
            setWsState('connected');  // Update WebSocket state
            transportQuality.onReconnectComplete(reconnectAttemptsRef.current);
            // NOTE: reconnectAttemptsRef is NOT reset here in ws.onopen. It will be
            // reset when the first valid message is received (handleWebSocketMessage),
            // confirming the backend accepted the connection. This prevents infinite
            // reconnection loops when the backend immediately rejects with code 1008
            // ("Session already has an active recording") — previously the counter
            // was reset to 0 here, so it never reached MAX_RECONNECT_ATTEMPTS.
            
            // FIX: Reset firstSegmentReceivedRef so the first-segment detection block
            // in handleWebSocketMessage fires on the reconnected WS. Without this,
            // firstSegmentReceivedRef stays true from the initial/previous connection,
            // the first-segment block never fires, and the reconnect counter never
            // resets — causing subsequent disconnects to continue counting from the
            // previous attempt number instead of starting fresh.
            firstSegmentReceivedRef.current = false;

            // FIX (Network Restore): Dismiss all stale connection-related notifications
            // from the disconnect/reconnecting phase before showing the success message.
            notification.destroy('connection-lost');
            notification.destroy('ws-error');
            notification.destroy('heartbeat-timeout');
            notification.destroy('transcript-inactive');

            notification.success({
              key: 'connection-restored',
              message: "Reconnected",
              description: "Connection restored. Your recording continues.",
              duration: 3,
            });

            // Auto-resume recording if it was paused due to disconnection
            // FIX (Stale Closure): Use pausedRef.current instead of closure-captured `paused` state
            if (pausedByDisconnectRef.current && pausedRef.current) {
              resumeRecording();
              pausedByDisconnectRef.current = false;
            }

            startTicking(); // Resume timer

            // Send configure message
            ws.send(JSON.stringify({
              type: "configure",
              config: {
                media_sample_rate_hz: 16000,
                audio_type: "CONVERSATION",
              },
            }));

            // Restart heartbeat after reconnection (CRITICAL FIX - Issue #1)
            if (heartbeatIntervalRef.current) {
              clearInterval(heartbeatIntervalRef.current);
              heartbeatIntervalRef.current = null;
            }
            if (heartbeatTimeoutRef.current) {
              clearTimeout(heartbeatTimeoutRef.current);
              heartbeatTimeoutRef.current = null;
            }

            // FIX (Stale Closure Audit — Bug #15): Add grace period to reconnect heartbeat,
            // matching the pattern used in startRecording's heartbeat. Previously, the reconnect
            // heartbeat went directly from first timeout to closing — no retry ping grace period.
            heartbeatIntervalRef.current = setInterval(() => {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: "ping" }));
                console.log('[WebSocket] Ping sent (after reconnect)');

                // Grace period: on first timeout, send a retry ping before closing
                heartbeatTimeoutRef.current = setTimeout(() => {
                  console.warn('[WebSocket] First heartbeat timeout (after reconnect) - sending retry ping before closing');

                  // Send a second ping as grace period
                  if (wsRef.current?.readyState === WebSocket.OPEN) {
                    wsRef.current.send(JSON.stringify({ type: "ping" }));
                    console.log('[WebSocket] Retry ping sent (grace period, after reconnect)');

                    // Second timeout — if still no pong after another HEARTBEAT_TIMEOUT, close
                    heartbeatTimeoutRef.current = setTimeout(() => {
                      console.error('[WebSocket] Heartbeat timeout - no pong after retry ping (after reconnect)');
                      notification.warning({
                        key: 'heartbeat-timeout',
                        message: ERROR_MESSAGES.HEARTBEAT_TIMEOUT.message,
                        description: ERROR_MESSAGES.HEARTBEAT_TIMEOUT.description,
                        duration: 5000,
                      });

                      console.warn('[WebSocket] Heartbeat timeout detected (after reconnect retry)', {
                        session_id,
                        patient_id,
                        elapsed_time: fmt(elapsedRef.current),
                        ws_ready_state: wsRef.current?.readyState,
                        last_pong: lastPongReceivedRef.current ? new Date(lastPongReceivedRef.current).toISOString() : 'never',
                      });

                      if (wsRef.current?.readyState === WebSocket.OPEN) {
                        console.warn('[WebSocket] Closing stale connection due to heartbeat timeout (after reconnect)');
                        wsRef.current.close(4003, 'Heartbeat timeout');
                      }
                    }, HEARTBEAT_TIMEOUT);
                  } else {
                    // WebSocket already closed — no need for retry
                    console.warn('[WebSocket] WebSocket not open during heartbeat timeout (after reconnect) — skipping retry');
                  }
                }, HEARTBEAT_TIMEOUT);
              }
            }, HEARTBEAT_INTERVAL);

            console.log('[WebSocket] Heartbeat monitoring restarted after reconnection (with grace period)');

            // Restart transcript inactivity monitoring after reconnection (FIX for Issue #5)
            startTranscriptInactivityMonitoring();
            console.log('[WebSocket] Transcript inactivity monitoring restarted after reconnection');
          };

          ws.onmessage = handleWebSocketMessage;
          ws.onerror = handleWebSocketError;
          // FIX (Stale Closure Audit — Circular Dep): Use ref indirection instead of
          // directly capturing handleWebSocketClose. This ensures the reconnected WebSocket
          // always gets the latest handleWebSocketClose, even though attemptReconnect's
          // useCallback identity doesn't depend on handleWebSocketClose anymore.
          // The ref is kept in sync on every render (see handleWebSocketCloseRef.current = ... below).
          ws.onclose = (...args) => handleWebSocketCloseRef.current?.(...args);

        } catch (error) {
          console.error("Reconnect failed:", error);
          // Telemetry: individual reconnection attempt failure
          metrics.add(metrics.websocketErrorEvent({
            was_recording: true,
            session_id,
            patient_id,
            cause: `reconnect_attempt_${reconnectAttemptsRef.current}_failed`,
          }));
          metrics.flush(); // Flush immediately — high-value domain error event

          // Note: ref was already incremented at the top of this setTimeout callback,
          // so no additional increment needed here. Just sync UI and retry.
          setReconnectAttempts(reconnectAttemptsRef.current);
          attemptReconnect(); // Try again with already-incremented counter
        }
      }, delay);
      // FIX (Stale Closure Audit — Circular Dep): Removed handleWebSocketClose from deps.
      // It is now accessed via handleWebSocketCloseRef (ref indirection), breaking the
      // circular useCallback dependency: attemptReconnect ↔ handleWebSocketClose.
      // session_id and patient_id are used in logging and telemetry within the callback body.
    }, [accounts, instance, WS_URL, handleWebSocketMessage, handleWebSocketError, startTicking, cleanupAudio, setRecordingParent, session_id, patient_id, transportQuality.onReconnectStart, transportQuality.onReconnectComplete]);

    // WebSocket close handler
    // FIX (Stale Closure Audit — Bug #7): Removed `recording` from dependency array
    // since the critical recording check uses `recordingRef.current` (not the state).
    // This makes the callback identity more stable and avoids the issue where
    // attemptReconnect (which was missing handleWebSocketClose in its deps) would
    // assign a stale version of this handler to reconnected WebSockets.
    const handleWebSocketClose = useCallback((event) => {
      // Clear first-segment timeout if active
      if (firstSegmentTimeoutRef.current) {
        clearTimeout(firstSegmentTimeoutRef.current);
        firstSegmentTimeoutRef.current = null;
      }

      // Clear initial WebSocket connection timeout if still pending
      if (wsConnectionTimeoutRef.current) {
        clearTimeout(wsConnectionTimeoutRef.current);
        wsConnectionTimeoutRef.current = null;
      }

      // Clear heartbeat interval and timeout
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      if (heartbeatTimeoutRef.current) {
        clearTimeout(heartbeatTimeoutRef.current);
        heartbeatTimeoutRef.current = null;
      }

      // Enhanced logging (use refs for values that may be stale in closures)
      console.log('[WebSocket] Connection closed', {
        code: event.code,
        reason: event.reason || 'No reason provided',
        wasClean: event.wasClean,
        intentional: intentionalCloseRef.current,
        recording_state: recordingRef.current,
        session_id,
        patient_id,
        elapsed_time: fmt(elapsedRef.current),
        first_segment_received: firstSegmentReceivedRef.current,
      });

      // Handle intentional close (user clicked stop)
      // FIX (Backend Code 1000): Only check intentionalCloseRef, NOT event.code === 1000.
      // Previously, `|| event.code === 1000` caused backend-initiated clean closes to be
      // treated as intentional, silently breaking the recording (no reconnection attempted).
      // All client-initiated ws.close(1000) calls already set intentionalCloseRef = true
      // or null out the onclose handler before closing, so this check is sufficient.
      if (intentionalCloseRef.current) {
        setWsState('disconnected');  // Update WebSocket state
        intentionalCloseRef.current = false;
        return;
      }

      // No client audio timeout — stop recording, don't auto-reconnect
      if (event.code === 4001) {
        console.warn('[WebSocket] No client audio timeout:', event.reason);
        // Telemetry: no client audio timeout (Gap #3)
        metrics.add(metrics.recordingInterruptedEvent({
          session_id,
          patient_id,
          duration_before_failure: Math.round(elapsedRef.current / 1000),
          cause: 'no_client_audio_timeout',
        }));
        metrics.flush();

        notification.warning({
          key: 'connection-lost',
          message: ERROR_MESSAGES.NO_CLIENT_AUDIO.message,
          description: ERROR_MESSAGES.NO_CLIENT_AUDIO.description,
          duration: 0,
        });
        stopTicking();
        cleanupAudio(true);
        setRecording(false);
        recordingRef.current = false;
        setRecordingParent(false);
        setWsState('disconnected');
        return;
      }

      // AWS Transcribe stream ended naturally — reconnect immediately (seamless)
      if (event.code === 4002 && recordingRef.current) {
        console.info('[WebSocket] Transcription stream ended, seamless reconnection');
        // Telemetry: transcribe stream ended (Gap #4)
        metrics.add(metrics.transcribeStreamEndedEvent({ session_id, patient_id }));

        setWsState('reconnecting');
        attemptReconnect({ immediate: true });
        return;
      }

      // EC-22 FIX: Use recordingRef instead of recording state to avoid stale closure
      if (recordingRef.current) {
        console.warn('[WebSocket] Unexpected disconnect - attempting reconnection');
        setWsState('reconnecting');  // Update WebSocket state
        stopTicking(); // Pause timer

        // FIX (Orphan WS): Auto-pause recording on unexpected disconnect to stop
        // AudioWorklet/ScriptProcessor from capturing and discarding audio during
        // the reconnection gap (2-9 seconds). Without this, audio is captured but
        // silently lost because ws.readyState !== OPEN. On successful reconnect,
        // the auto-resume in ws.onopen (via pausedByDisconnectRef) will send a
        // "resume" message to the backend and restart audio capture.
        if (!pausedRef.current) {
          setPaused(true);
          pausedRef.current = true;
          pausedByDisconnectRef.current = true;

          // Pause AudioWorklet if active
          if (processorRef.current && processorRef.current.port) {
            try { processorRef.current.port.postMessage({ type: 'pause' }); } catch { }
          }

          // Clear transcript inactivity monitoring during disconnect
          if (transcriptInactivityTimeoutRef.current) {
            clearTimeout(transcriptInactivityTimeoutRef.current);
            transcriptInactivityTimeoutRef.current = null;
          }
          notification.destroy('transcript-inactive');
          transcriptInactivityCountRef.current = 0;
          transcriptInactivityActiveRef.current = false;

          // Telemetry: auto-pause on disconnect (Gap #10)
          metrics.add(metrics.recordingPauseResumeEvent({
            session_id, patient_id, action: 'paused', trigger: 'disconnect',
            duration_seconds: Math.round(elapsedRef.current / 1000),
          }));

          console.log('[WebSocket] Recording auto-paused due to unexpected disconnect');
        }

        // Telemetry: unexpected WebSocket disconnect during recording
        metrics.add(metrics.websocketErrorEvent({
          close_code: event.code,
          close_reason: event.reason,
          was_recording: true,
          session_id,
          patient_id,
          cause: 'unexpected_disconnect',
        }));

        // Telemetry: recording interrupted
        metrics.add(metrics.recordingInterruptedEvent({
          session_id,
          patient_id,
          duration_before_failure: Math.round(elapsedRef.current / 1000),
          cause: `WebSocket closed unexpectedly (code: ${event.code}, reason: ${event.reason || 'none'})`,
        }));
        metrics.flush(); // Flush immediately — high-value domain error events

        notification.warning({
          key: 'connection-lost',
          message: ERROR_MESSAGES.CONNECTION_LOST.message,
          description: ERROR_MESSAGES.CONNECTION_LOST.description,
          duration: 0,
        });

        attemptReconnect();
      } else {
        console.log('[WebSocket] Connection closed while not recording - no reconnect needed');
      }
    }, [stopTicking, attemptReconnect, session_id, patient_id]);

    // FIX (Stale Closure Audit — Circular Dep): Keep ref in sync with the latest
    // handleWebSocketClose so attemptReconnect can use it via ref indirection.
    handleWebSocketCloseRef.current = handleWebSocketClose;

    const handleStartClick = async () => {
      if (recording) return;
      const ok = await requestConsent?.();
      if (!ok) return;
      startRecording();
    };

    // EC-22 FIX: Helper to rollback all recording state on startup failure
    const rollbackRecordingState = useCallback((reason) => {
      console.error(`[Recording] Startup failed: ${reason}`);
      setRecording(false);
      recordingRef.current = false;
      setRecordingParent(false);
      setPaused(false);
      pausedRef.current = false;
      setWsState('idle');
      stopTicking();
      elapsedRef.current = 0;
      pushTimer(0);
      cleanupAudio(true);

      // Clear any WS that may have been opened
      if (wsRef.current) {
        intentionalCloseRef.current = true;
        try { wsRef.current.close(1000, reason); } catch { }
        wsRef.current = null;
      }

      // Clear all timers
      if (heartbeatIntervalRef.current) { clearInterval(heartbeatIntervalRef.current); heartbeatIntervalRef.current = null; }
      if (heartbeatTimeoutRef.current) { clearTimeout(heartbeatTimeoutRef.current); heartbeatTimeoutRef.current = null; }
      if (transcriptInactivityTimeoutRef.current) { clearTimeout(transcriptInactivityTimeoutRef.current); transcriptInactivityTimeoutRef.current = null; }
      if (wsConnectionTimeoutRef.current) { clearTimeout(wsConnectionTimeoutRef.current); wsConnectionTimeoutRef.current = null; }

      // Release Web Lock and clear AudioContext watchdog
      if (wakeLockReleaseRef.current) { wakeLockReleaseRef.current(); wakeLockReleaseRef.current = null; }
      if (audioContextWatchdogRef.current) { clearInterval(audioContextWatchdogRef.current); audioContextWatchdogRef.current = null; }
    }, []);

    const startRecording = async () => {
      if (recording) return;
      setRecording(true);
      recordingRef.current = true; // EC-22 FIX: Keep ref in sync
      setRecordingParent(true);
      setFetchedTranscript(null);
      setPaused(false);
      pausedRef.current = false;
      intentionalCloseRef.current = false;
      reconnectAttemptsRef.current = 0; // Reset ref (source of truth)
      setReconnectAttempts(0); // Sync parent state for UI
      reconnectTimestampsRef.current = [];
      setWsConnectionFailed(false); // Reset WebSocket connection failed state on new recording
      // FIX (Stale Closure Audit — Bug #9-13): Reset refs that were not being reset on fresh start.
      // These could carry stale values from a previous recording session.
      lastPongReceivedRef.current = null;
      lastTranscriptTimeRef.current = null;
      transcriptInactivityCountRef.current = 0;
      transcriptInactivityActiveRef.current = false;
      pausedByDisconnectRef.current = false;
      // FIX (Stale Closure Audit — Bug #F): Reset firstSegmentReceivedRef on fresh start.
      // Previously only reset inside ws.onopen, leaving a stale `true` value between
      // startRecording() call and ws.onopen if a previous recording had set it.
      firstSegmentReceivedRef.current = false;
      // Reset timestamp offset - will be set from API response if recovering from previous session
      timestampOffsetRef.current = 0;

      // FIX (RCA 5B): Unconditionally reset elapsed time and timer on fresh start.
      // Previously, elapsedRef was only reset conditionally (`if (elapsedRef.current === 0)`),
      // which could leave stale timer values from a previous session.
      elapsedRef.current = 0;
      pushTimer(0);

      // FIX (RCA 5B): Clear stale transcript buffers from any previous session
      livePreviewRef.current.clear();
      lastCommittedRef.current.clear();
      sentenceBufferRef.current.clear();
      publishLivePreview();

      // Fetch same-day recording before starting WebSocket
      try {
        console.log('[Recording] Checking for same-day recording...');
        const recordingTranscriptResponse = await axiosInstance.get(
          `patient/${patient_id}/sessions/${session_id}/recording-transcript`,
          { validateStatus: (status) => status !== 401 }
        );

        if (recordingTranscriptResponse.status === 200 && recordingTranscriptResponse.data?.content) {
          const decoded = decodeBase64(recordingTranscriptResponse.data.content);

          // Calculate character count for notification
          const charCount = decoded.length;

          // Extract last timestamp if available for offset
          // Look for patterns like [12.34s] or [00:12-00:34]
          const timestampMatches = decoded.match(/\[(\d+(?:\.\d+)?)[s\]]/g);
          if (timestampMatches && timestampMatches.length > 0) {
            const lastMatch = timestampMatches[timestampMatches.length - 1];
            const lastTimestamp = parseFloat(lastMatch.match(/\d+(?:\.\d+)?/)[0]);
            timestampOffsetRef.current = lastTimestamp;
            console.log(`[Recording] Setting timestamp offset to ${lastTimestamp}s from recovered transcript`);
          }

          // Display notification
          notification.info({
            message: "Previous Recording Recovered",
            description: `Recovered ${charCount} characters from your previous session.`,
          });

          // Prepend to recording transcript with visual separator
          setRecordingTranscript([
            {
              type: "recovered",
              transcript: decoded,
              timestamp: "Previous Session",
              isRecovered: true,
            },
            {
              type: "separator",
              transcript: "--- Continued Recording ---",
              timestamp: "",
              isSeparator: true,
            },
          ]);

          // Telemetry: same-day recording recovered (Gap #12)
          metrics.add(metrics.recordingRecoveredEvent({
            session_id,
            patient_id,
            char_count: charCount,
            timestamp_offset: timestampOffsetRef.current,
          }));

          console.log('[Recording] Same-day recording recovered successfully');
        } else if (recordingTranscriptResponse.status === 204) {
          console.log('[Recording] No same-day recording found, starting fresh');
        }
      } catch (err) {
        console.log('[Recording] No previous same-day recording found or error fetching:', err?.response?.status || err.message);
        // Continue with fresh recording
      }

      // FIX (RCA 4A/4D): Use TokenManager for token acquisition instead of
      // direct acquireTokenSilent. TokenManager returns a proactively-refreshed
      // cached token instantly, or performs a silent refresh with retries.
      // This ensures WebSocket connections always get a fresh token, even for
      // sessions lasting 2-4+ hours.
      let token;
      try {
        token = await tokenManager.getToken();
        if (!token) {
          throw new Error('TokenManager returned no token');
        }
      } catch (tokenError) {
        // Telemetry: token acquisition failure during recording startup
        metrics.add(metrics.authErrorEvent({
          error_type: 'token_acquisition_failed',
          silent_or_interactive: 'silent',
          error_message: tokenError?.message || String(tokenError),
        }));
        metrics.flush(); // Flush immediately — high-value domain error event

        rollbackRecordingState('Token acquisition failed');
        notification.error({
          message: 'Authentication Error',
          description: 'Could not authenticate. Please try logging in again.',
          duration: 0,
        });
        return;
      }

      // EC-2 FIX: Acquire microphone BEFORE opening WebSocket (EC-1 FIX)
      // This prevents false transcript inactivity warnings while user is granting mic permission
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
      } catch (micError) {
        // Telemetry: microphone access failure during recording startup
        metrics.add(metrics.recordingInterruptedEvent({
          session_id,
          patient_id,
          duration_before_failure: 0,
          cause: `Microphone access denied: ${micError.name} — ${micError.message}`,
        }));
        metrics.flush(); // Flush immediately — high-value domain error event

        rollbackRecordingState('Microphone access denied');
        notification.error({
          message: 'Microphone Access Required',
          description: micError.name === 'NotAllowedError'
            ? 'Microphone permission was denied. Please allow microphone access and try again.'
            : micError.name === 'NotFoundError'
              ? 'No microphone found. Please connect a microphone and try again.'
              : `Could not access microphone: ${micError.message}`,
          duration: 0,
        });
        return;
      }

      // EC-5 FIX: Don't start timer here — start it in ws.onopen instead
      // so timer only runs when connection is actually established

      cleanupAudio(false);

      // FIX (Orphan WS): Close any lingering WebSocket from a previous recording session.
      // ws.close() is asynchronous — if the user clicks Start quickly after Stop, the old
      // WS may still be in CLOSING state. Additionally, stopRecording only closes WS if
      // readyState === OPEN, missing CONNECTING state. Null out handlers first to prevent
      // the close event from triggering reconnection logic.
      if (wsRef.current) {
        console.warn('[WebSocket] Closing lingering WebSocket before starting new recording');
        try {
          wsRef.current.onclose = null;
          wsRef.current.onerror = null;
          wsRef.current.onmessage = null;
          intentionalCloseRef.current = true;
          wsRef.current.close(1000, "Starting new recording");
        } catch { }
        wsRef.current = null;
      }

      const ws = new WebSocket(WS_URL, [`Bearer-${token}`, "dfeeergvcdcsdf"]);
      wsRef.current = ws;

      // Initial WebSocket connection timeout — if onopen doesn't fire within 15 seconds,
      // notify the user and rollback recording state
      const WS_CONNECT_TIMEOUT = 15000;
      wsConnectionTimeoutRef.current = setTimeout(() => {
        // Guard: Skip if recording was intentionally stopped or WS was already cleaned up
        if (intentionalCloseRef.current || !wsRef.current) return;
        if (wsRef.current.readyState !== WebSocket.OPEN) {
          console.error('[WebSocket] Initial connection timeout — server did not respond within 15 seconds');

          // Telemetry: WebSocket initial connection timeout
          metrics.add(metrics.websocketErrorEvent({
            was_recording: true,
            session_id,
            patient_id,
            cause: 'initial_connection_timeout',
          }));
          metrics.add(metrics.recordingInterruptedEvent({
            session_id,
            patient_id,
            duration_before_failure: 0,
            cause: 'WebSocket initial connection timeout (15s)',
          }));
          metrics.flush(); // Flush immediately — high-value domain error events

          rollbackRecordingState('WebSocket connection timeout');
          notification.error({
            key: 'ws-connect-timeout',
            message: ERROR_MESSAGES.WEBSOCKET_NOT_CONNECTED.message,
            description: ERROR_MESSAGES.WEBSOCKET_NOT_CONNECTED.description,
            duration: 0,
          });
          // Close the pending WebSocket
          try { wsRef.current.close(); } catch { }
          wsRef.current = null;
        }
      }, WS_CONNECT_TIMEOUT);

      ws.onopen = () => {
        // Clear the initial connection timeout — we connected successfully
        if (wsConnectionTimeoutRef.current) {
          clearTimeout(wsConnectionTimeoutRef.current);
          wsConnectionTimeoutRef.current = null;
        }

        // EC-5 FIX: Start timer only after WebSocket is connected
        startTicking();
        setWsState('connected');  // Update WebSocket state

        // Telemetry: recording started successfully (Gap #7)
        metrics.add(metrics.recordingStartedEvent({
          session_id,
          patient_id,
          had_previous_recording: timestampOffsetRef.current > 0,
        }));

        // Reset first segment tracking (for logging purposes)
        firstSegmentReceivedRef.current = false;

        // Note: First-segment detection is now handled by the recurring
        // transcript inactivity monitoring (startTranscriptInactivityMonitoring)
        // which serves both initial audio detection and ongoing transcript monitoring

        ws.send(
          JSON.stringify({
            type: "configure",
            config: {
              media_sample_rate_hz: 16000,
              audio_type: "CONVERSATION",
            },
          })
        );

        // Start heartbeat (ping every 15 seconds)
        heartbeatIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: "ping" }));
            console.log('[WebSocket] Ping sent');

            // FIX (RCA 1A): Grace period — on first timeout, send a retry ping
            // instead of immediately closing. Only close on second consecutive timeout.
            // This prevents unnecessary reconnections from a single dropped packet.
            heartbeatTimeoutRef.current = setTimeout(() => {
              console.warn('[WebSocket] First heartbeat timeout - sending retry ping before closing');

              // Send a second ping as grace period
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: "ping" }));
                console.log('[WebSocket] Retry ping sent (grace period)');

                // Second timeout — if still no pong after another HEARTBEAT_TIMEOUT, close
                heartbeatTimeoutRef.current = setTimeout(() => {
                  console.error('[WebSocket] Heartbeat timeout - no pong after retry ping');
                  // Telemetry: heartbeat timeout (Gap #5)
                  metrics.add(metrics.heartbeatTimeoutEvent({
                    session_id,
                    patient_id,
                    elapsed_seconds: Math.round(elapsedRef.current / 1000),
                    last_pong_ago: lastPongReceivedRef.current ? Math.round((Date.now() - lastPongReceivedRef.current) / 1000) : null,
                  }));

                  notification.warning({
                    key: 'heartbeat-timeout',
                    message: ERROR_MESSAGES.HEARTBEAT_TIMEOUT.message,
                    description: ERROR_MESSAGES.HEARTBEAT_TIMEOUT.description,
                    duration: 5000,
                  });

                  console.warn('[WebSocket] Heartbeat timeout detected (after retry)', {
                    session_id,
                    patient_id,
                    elapsed_time: fmt(elapsedRef.current),
                    ws_ready_state: wsRef.current?.readyState,
                    last_pong: lastPongReceivedRef.current ? new Date(lastPongReceivedRef.current).toISOString() : 'never',
                  });

                      if (wsRef.current?.readyState === WebSocket.OPEN) {
                        console.warn('[WebSocket] Closing stale connection due to heartbeat timeout');
                        wsRef.current.close(4003, 'Heartbeat timeout');
                      }
                    }, HEARTBEAT_TIMEOUT);
              } else {
                // WebSocket already closed — no need for retry
                console.warn('[WebSocket] WebSocket not open during heartbeat timeout — skipping retry');
              }
            }, HEARTBEAT_TIMEOUT);
          }
        }, 15000); // Send ping every 15 seconds

        // Initialize transcript inactivity monitoring (FIX for Issue #5)
        startTranscriptInactivityMonitoring();
        console.log('[WebSocket] Transcript inactivity monitoring started');
      };

      // Use extracted handlers for reusability during reconnect
      ws.onmessage = handleWebSocketMessage;
      ws.onerror = handleWebSocketError;
      ws.onclose = handleWebSocketClose;

      // EC-16 FIX: Add OS-level mute detection listeners on mic stream
      try {
        const micTrack = stream.getAudioTracks()[0];
        if (micTrack) {
          micTrack.onmute = () => {
            console.warn('[Microphone] OS-level mute detected');
            // Telemetry: microphone muted (Gap #15)
            metrics.add(metrics.microphoneEvent({ session_id, patient_id, event_name: 'muted' }));
            notification.warning({
              key: 'mic-muted',
              message: 'Microphone Muted',
              description: 'Your microphone appears to be muted. Please unmute to continue recording.',
              duration: 0,
            });
          };
          micTrack.onunmute = () => {
            console.log('[Microphone] OS-level unmute detected');
            // Telemetry: microphone unmuted (Gap #15)
            metrics.add(metrics.microphoneEvent({ session_id, patient_id, event_name: 'unmuted' }));
            notification.destroy('mic-muted');
          };
        }
      } catch (err) {
        console.warn('[Microphone] Failed to set up mute detection listeners:', err);
      }

      // EC-1/EC-2 FIX: Microphone already acquired above (before WebSocket)
      // Create AudioContext without fixed sampleRate to avoid Firefox compatibility issues
      // Firefox doesn't support connecting MediaStreamSource to AudioContext with different sample rates
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      sourceRef.current = source;

      // ============================================================================
      // BACKGROUND TAB PROTECTION: Web Lock API + AudioContext Watchdog
      // Prevents Chrome from auto-suspending AudioContext when tab is backgrounded
      // ============================================================================

      // Technique 1: Web Lock API — holds a lock that prevents Chrome from
      // fully throttling the tab, keeping AudioContext active in background
      if (navigator.locks) {
        try {
          navigator.locks.request('recording-audio-active', { mode: 'exclusive' }, () => {
            console.log('[WebLock] Acquired recording lock — tab will resist background throttling');
            return new Promise((resolve) => {
              wakeLockReleaseRef.current = resolve; // Store resolve to release lock on stop
            });
          });
        } catch (err) {
          console.warn('[WebLock] Failed to acquire lock:', err);
        }
      }

      // Technique 2: AudioContext Watchdog — periodic check that resumes
      // AudioContext if Chrome suspends it despite the Web Lock
      // Note: setInterval is throttled to ~1/min in background tabs, but that's
      // still enough to catch and resume a suspended AudioContext within ~1 minute
      lastAudioChunkSentRef.current = Date.now(); // Baseline: recording just started

      audioContextWatchdogRef.current = setInterval(() => {
        if (audioContextRef.current && recordingRef.current && !pausedRef.current) {
          const ctx = audioContextRef.current;
          // Check 1: Resume if explicitly suspended by browser
          if (ctx.state === 'suspended') {
            console.warn('[AudioContext Watchdog] Detected suspension during active recording, resuming...');
            // Telemetry: AudioContext resumed by watchdog (Gap #18)
            metrics.add(metrics.audioProcessingEvent({ session_id, patient_id, event_name: 'context_resumed_by_watchdog' }));
            ctx.resume().catch(err => {
              console.error('[AudioContext Watchdog] Failed to resume:', err);
            });
          }
          // Check 2: Detect audio gap — no chunks sent despite 'running' state
          // This catches partial suspensions where Chrome stops delivering audio
          // without fully suspending the AudioContext (common during video playback)
          const lastChunk = lastAudioChunkSentRef.current;
          if (lastChunk && ctx.state !== 'suspended') {
            const gapSeconds = (Date.now() - lastChunk) / 1000;
            if (gapSeconds > 5) {
              console.warn(`[AudioContext Watchdog] Audio gap detected: ${gapSeconds.toFixed(1)}s without chunks (state: ${ctx.state}) — resuming`);
              metrics.add(metrics.audioProcessingEvent({
                session_id, patient_id,
                event_name: 'context_resumed_by_gap_detection',
                detail: `gap_${Math.round(gapSeconds)}s`,
              }));
              ctx.resume().catch(err => {
                console.error('[AudioContext Watchdog] Gap-detection resume failed:', err);
              });
              lastAudioChunkSentRef.current = Date.now(); // Reset so we don't re-fire on every tick for the same gap
            }
          }
        }
      }, 5000); // Check every 5 seconds (throttled to ~1/min in background)

      // VIDEO PLAYBACK PROTECTION: Resume AudioContext immediately when a video/audio
      // element starts playing in the same tab. Chrome can briefly suspend the recording
      // AudioContext when routing audio output for video playback.
      const handleVideoPlay = (e) => {
        if (e.target.tagName === 'VIDEO' || e.target.tagName === 'AUDIO') {
          if (audioContextRef.current && audioContextRef.current.state === 'suspended' && recordingRef.current && !pausedRef.current) {
            console.log('[AudioContext] Video/audio play detected — resuming suspended AudioContext');
            metrics.add(metrics.audioProcessingEvent({ session_id, patient_id, event_name: 'context_resumed_by_video_play' }));
            audioContextRef.current.resume().catch(err => {
              console.error('[AudioContext] Failed to resume on video play:', err);
            });
          }
        }
      };
      document.addEventListener('play', handleVideoPlay, true); // capture phase
      videoPlayListenerRef.current = handleVideoPlay;

      // ============================================================================
      // AUDIOWORKLET MIGRATION (Phase 1 - Task 1.2)
      // Replace deprecated createScriptProcessor with modern AudioWorklet API
      // ============================================================================

      // Feature detection: Check if AudioWorklet is supported
      if (audioContext.audioWorklet) {
        try {
          console.log('[AudioWorklet] Loading worklet module...');

          // Load AudioWorklet processor module
          await audioContext.audioWorklet.addModule('/audio-processor.worklet.js');

          console.log('[AudioWorklet] Worklet module loaded successfully');

          // Create AudioWorkletNode
          const workletNode = new AudioWorkletNode(
            audioContext,
            'audio-recording-processor'
          );

          // Store reference for cleanup
          processorRef.current = workletNode;

          // Handle messages from worklet (processed audio data)
          workletNode.port.onmessage = (event) => {
            const { type, data } = event.data;

            if (type === 'audio_data') {
              // Extra safety: skip sending if paused (belt-and-suspenders with worklet's own pause check)
              if (pausedRef.current) return;
              const ws = wsRef.current;
              if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(data);
                lastAudioChunkSentRef.current = Date.now();
                transportQuality.onAudioSent();
              }
            }
          };

          // Connect audio graph: source → worklet → destination
          source.connect(workletNode);
          workletNode.connect(audioContext.destination);

          console.log('[AudioWorklet] Audio graph connected successfully');

        } catch (error) {
          console.error('[AudioWorklet] Failed to load worklet, falling back to ScriptProcessor:', error);

          // Telemetry: AudioWorklet fallback to ScriptProcessor (Gap #17)
          metrics.add(metrics.audioProcessingEvent({
            session_id, patient_id,
            event_name: 'worklet_fallback',
            detail: error?.message || 'AudioWorklet load failed',
          }));

          // Show warning to user
          notification.warning({
            message: 'Using Legacy Audio Processing',
            description: 'AudioWorklet failed to load. Background recording may not work. Please keep this tab active.',
            duration: 10,
          });

          // EC-3 FIX: Wrap ScriptProcessor fallback in try/catch
          try {
            setupScriptProcessor(audioContext, source);
          } catch (fallbackError) {
            console.error('[ScriptProcessor] Fallback also failed:', fallbackError);
            // Telemetry: audio processing setup failed (fatal)
            metrics.add(metrics.audioProcessingEvent({ session_id, patient_id, event_name: 'setup_failed', detail: fallbackError?.message || 'ScriptProcessor fallback failed' }));
            metrics.flush();
            rollbackRecordingState('Audio processing setup failed');
            notification.error({
              message: 'Audio Processing Failed',
              description: 'Could not set up audio processing. Please try refreshing the page.',
              duration: 0,
            });
          }
        }
      } else {
        console.warn('[AudioWorklet] Not supported in this browser, using deprecated ScriptProcessor');

        // Telemetry: AudioWorklet not supported in browser
        metrics.add(metrics.audioProcessingEvent({ session_id, patient_id, event_name: 'worklet_not_supported' }));

        // Show warning to user
        notification.warning({
          message: 'Limited Browser Support',
          description: 'Background recording may not work. Please keep this tab active and consider updating your browser.',
          duration: 10,
        });

        // EC-3 FIX: Wrap ScriptProcessor setup in try/catch
        try {
          setupScriptProcessor(audioContext, source);
        } catch (spError) {
          console.error('[ScriptProcessor] Setup failed:', spError);
          // Telemetry: audio processing setup failed (fatal)
          metrics.add(metrics.audioProcessingEvent({ session_id, patient_id, event_name: 'setup_failed', detail: spError?.message || 'ScriptProcessor setup failed' }));
          metrics.flush();
          rollbackRecordingState('Audio processing setup failed');
          notification.error({
            message: 'Audio Processing Failed',
            description: 'Could not set up audio processing. Please try refreshing the page.',
            duration: 0,
          });
        }
      }
    };

    // Helper function to setup legacy ScriptProcessor (fallback)
    const setupScriptProcessor = (audioContext, source) => {
      console.log('[ScriptProcessor] Setting up legacy audio processing');

      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      // Target sample rate for the backend
      const TARGET_SAMPLE_RATE = 16000;
      const nativeSampleRate = audioContext.sampleRate;

      processor.onaudioprocess = (e) => {
        if (pausedRef.current) return;
        const input = e.inputBuffer.getChannelData(0);

        // Resample from native rate to 16000 Hz if needed
        let resampled;
        if (nativeSampleRate !== TARGET_SAMPLE_RATE) {
          const ratio = nativeSampleRate / TARGET_SAMPLE_RATE;
          const newLength = Math.round(input.length / ratio);
          resampled = new Float32Array(newLength);
          for (let i = 0; i < newLength; i++) {
            const srcIndex = i * ratio;
            const srcIndexFloor = Math.floor(srcIndex);
            const srcIndexCeil = Math.min(srcIndexFloor + 1, input.length - 1);
            const t = srcIndex - srcIndexFloor;
            // Linear interpolation for smoother resampling
            resampled[i] = input[srcIndexFloor] * (1 - t) + input[srcIndexCeil] * t;
          }
        } else {
          resampled = input;
        }

        // Convert to 16-bit PCM
        const pcm = new Int16Array(resampled.length);
        for (let i = 0; i < resampled.length; i++) {
          let s = Math.max(-1, Math.min(1, resampled[i]));
          pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(pcm.buffer);
          lastAudioChunkSentRef.current = Date.now();
          transportQuality.onAudioSent();
        }
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      console.log('[ScriptProcessor] Legacy audio processing setup complete');
    };

    const pauseRecording = () => {
      // FIX (Stale Closure): Use refs instead of state for guard check.
      // This function may be called from closures (e.g., network event handlers)
      // where the captured `recording` and `paused` state values are stale.
      if (!recordingRef.current || pausedRef.current) return;
      setPaused(true);
      pausedRef.current = true;

      // Telemetry: recording paused by user (Gap #9)
      metrics.add(metrics.recordingPauseResumeEvent({
        session_id, patient_id, action: 'paused', trigger: 'user',
        duration_seconds: Math.round(elapsedRef.current / 1000),
      }));

      stopTicking();
      if (startRef.current) {
        elapsedRef.current = Date.now() - startRef.current;
        pushTimer(elapsedRef.current);
      }

      // FIX (Issue #5 + Silence Recovery): Clear transcript inactivity timeout when paused
      // and dismiss any existing inactivity notification since pause is intentional
      if (transcriptInactivityTimeoutRef.current) {
        clearTimeout(transcriptInactivityTimeoutRef.current);
        transcriptInactivityTimeoutRef.current = null;
      }
      notification.destroy('transcript-inactive');
      transcriptInactivityCountRef.current = 0;
      transcriptInactivityActiveRef.current = false;

      // Send pause message to worklet (if using AudioWorklet)
      if (processorRef.current && processorRef.current.port) {
        processorRef.current.port.postMessage({ type: 'pause' });
      }

      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "pause" }));
      }
    };

    const resumeRecording = () => {
      // FIX (Stale Closure): Use refs instead of state for guard check.
      // This function may be called from closures (e.g., ws.onopen after reconnect,
      // handleNetworkRestored) where the captured `recording` and `paused` state
      // values are stale. Refs always reflect the latest value.
      if (!recordingRef.current || !pausedRef.current) return;
      setPaused(false);
      pausedRef.current = false;

      // EC-10 FIX: Resume AudioContext if it was suspended (e.g., after tab backgrounding)
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        console.log('[AudioContext] Resuming suspended AudioContext on recording resume');
        audioContextRef.current.resume().catch(err => {
          console.error('[AudioContext] Failed to resume:', err);
        });
      }

      // EC-11 FIX: Check if mic track ended during pause (e.g., device disconnected)
      if (streamRef.current) {
        const track = streamRef.current.getAudioTracks()[0];
        if (track && track.readyState === 'ended') {
          console.warn('[Microphone] Track ended during pause, attempting to re-acquire');
          // Telemetry: microphone disconnected during pause (Gap #16)
          metrics.add(metrics.microphoneEvent({ session_id, patient_id, event_name: 'disconnected' }));
          notification.warning({
            key: 'mic-reconnect',
            message: 'Microphone Disconnected',
            description: 'Your microphone was disconnected. Attempting to reconnect...',
            duration: 5,
          });
          // Attempt to re-acquire microphone
          navigator.mediaDevices.getUserMedia({ audio: true }).then(newStream => {
            streamRef.current = newStream;
            // Reconnect audio graph
            if (audioContextRef.current && sourceRef.current) {
              try { sourceRef.current.disconnect(); } catch { }
              const newSource = audioContextRef.current.createMediaStreamSource(newStream);
              sourceRef.current = newSource;
              if (processorRef.current) {
                newSource.connect(processorRef.current);
              }
            }
            notification.destroy('mic-reconnect');
            notification.success({ message: 'Microphone reconnected', duration: 3 });
            // Telemetry: mic reconnect success
            metrics.add(metrics.microphoneEvent({ session_id, patient_id, event_name: 'reconnected' }));
            console.log('[Microphone] Successfully re-acquired after track ended');
          }).catch(err => {
            console.error('[Microphone] Failed to re-acquire:', err);
            // Telemetry: mic reconnect failure
            metrics.add(metrics.microphoneEvent({ session_id, patient_id, event_name: 'reconnect_failed', detail: err?.message }));
            notification.error({
              key: 'mic-reconnect',
              message: 'Microphone Unavailable',
              description: 'Could not reconnect microphone. Recording may capture silence.',
              duration: 0,
            });
          });
        }
      }

      // Telemetry: recording resumed by user (Gap #9)
      metrics.add(metrics.recordingPauseResumeEvent({
        session_id, patient_id, action: 'resumed', trigger: 'user',
        duration_seconds: Math.round(elapsedRef.current / 1000),
      }));

      startTicking();

      // FIX (Issue #5 + Silence Recovery): Restart transcript inactivity monitoring when resumed
      // Use grace period (45s) since the backend transcription pipeline needs time to
      // restart after a pause, especially for long pauses (e.g., 60+ seconds of silence)
      startTranscriptInactivityMonitoring(true);

      // Send resume message to worklet (if using AudioWorklet)
      if (processorRef.current && processorRef.current.port) {
        processorRef.current.port.postMessage({ type: 'resume' });
      }

      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "resume" }));
      }
    };

    const stopRecording = () => {
      intentionalCloseRef.current = true; // Mark as intentional close to prevent reconnect
      setRecording(false);
      recordingRef.current = false; // EC-22 FIX: Keep ref in sync on stop
      setRecordingParent(false);
      setPaused(false);
      pausedRef.current = false;
      setWsState('idle');  // Reset WebSocket state to idle
      stopTicking();

      // Reset WebSocket connection failed state when user manually stops
      setWsConnectionFailed(false);

      // Reset all tracking refs (FIX for Issues #3, #4, #5, #6 + Silence Recovery)
      pausedByDisconnectRef.current = false;
      timestampOffsetRef.current = 0;
      firstSegmentReceivedRef.current = false;
      lastPongReceivedRef.current = null;
      lastTranscriptTimeRef.current = null;
      transcriptInactivityCountRef.current = 0;
      transcriptInactivityActiveRef.current = false;
      reconnectAttemptsRef.current = 0; // Reset ref (source of truth)
      setReconnectAttempts(0); // Sync parent state for UI
      reconnectTimestampsRef.current = [];

      // Clear any pending reconnect
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Clear first-segment timeout
      if (firstSegmentTimeoutRef.current) {
        clearTimeout(firstSegmentTimeoutRef.current);
        firstSegmentTimeoutRef.current = null;
      }

      // Clear heartbeat interval
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }

      // Clear heartbeat timeout
      if (heartbeatTimeoutRef.current) {
        clearTimeout(heartbeatTimeoutRef.current);
        heartbeatTimeoutRef.current = null;
      }

      // Clear transcript inactivity timeout (FIX for Issue #5)
      if (transcriptInactivityTimeoutRef.current) {
        clearTimeout(transcriptInactivityTimeoutRef.current);
        transcriptInactivityTimeoutRef.current = null;
      }

      // Clear initial WebSocket connection timeout (edge case: stop during CONNECTING state)
      if (wsConnectionTimeoutRef.current) {
        clearTimeout(wsConnectionTimeoutRef.current);
        wsConnectionTimeoutRef.current = null;
      }

      // Release Web Lock and clear AudioContext watchdog on stop
      if (wakeLockReleaseRef.current) {
        wakeLockReleaseRef.current();
        wakeLockReleaseRef.current = null;
        console.log('[WebLock] Released recording lock');
      }
      if (audioContextWatchdogRef.current) {
        clearInterval(audioContextWatchdogRef.current);
        audioContextWatchdogRef.current = null;
      }
      if (videoPlayListenerRef.current) {
        document.removeEventListener('play', videoPlayListenerRef.current, true);
        videoPlayListenerRef.current = null;
      }
      lastAudioChunkSentRef.current = null;

      // EC-12 FIX: Send stop message to worklet to flush remaining buffer before cleanup
      if (processorRef.current && processorRef.current.port) {
        try {
          processorRef.current.port.postMessage({ type: 'stop' });
        } catch { }
      }

      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ type: "end_audio" }));
        } catch { }
        try {
          ws.close(1000, "User stopped recording"); // Normal closure
        } catch { }
      }
      wsRef.current = null;

      // Clear recording transcript for next session
      // setRecordingTranscript([]);

      livePreviewRef.current.clear();
      publishLivePreview();
      cleanupAudio(true);

      // Telemetry: recording stopped by user (Gap #8)
      metrics.add(metrics.recordingStoppedEvent({
        session_id, patient_id,
        duration_seconds: Math.round(elapsedRef.current / 1000),
      }));

      // Dismiss any connection lost notifications
      notification.destroy('connection-lost');

      try {
        onStopped?.();
      } catch (err) {
        console.error('[Recording] onStopped callback failed:', err);
      }
      try {
        window.dispatchEvent(new CustomEvent('ai-notes:nudge', { detail: { reason: 'stopped' } }));
      } catch (err) {
        console.warn('[Recording] Failed to dispatch ai-notes:nudge event:', err);
      }
    };

    useEffect(() => {
      if (parentRecording) handleStartClick();
    }, []);

    const isDisabledByEhr = isNoteSigned || isEhrSubmitted || (isValidAppointment === false);
    // FIX: Disable recording/upload buttons while AI notes are being generated or polled
    const isGeneratingAiNotes = generatingNotes || pollingNote;

    // ============================================================================
    // PAGE VISIBILITY API (Phase 2 - Task 2.1)
    // Detect tab backgrounding and notify users
    // ============================================================================
    useEffect(() => {
      if (!recording) return;

      const handleVisibilityChange = () => {
        if (document.hidden) {
          console.warn('[Visibility] Tab backgrounded during recording', {
            session_id,
            patient_id,
            elapsed_time: fmt(elapsedRef.current),
            timestamp: new Date().toISOString(),
          });

          // Show persistent warning notification
          notification.warning({
            key: 'tab-backgrounded',
            message: 'Recording in Background',
            description: 'Keep this tab open to ensure recording continues. Your recording is still active.',
            duration: 0, // Don't auto-dismiss
          });

          // Telemetry: page visibility change (recording-aware)
          metrics.add(metrics.pageVisibilityChangeEvent({
            visible: false,
            was_recording: true,
            session_id,
            patient_id,
          }));
        } else {
          console.log('[Visibility] Tab visible again', {
            session_id,
            patient_id,
            elapsed_time: fmt(elapsedRef.current),
            timestamp: new Date().toISOString(),
          });

          // Clear warning notification
          notification.destroy('tab-backgrounded');

          // EC-10/EC-19 FIX: Resume AudioContext if suspended while tab was backgrounded
          if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            console.log('[Visibility] Resuming suspended AudioContext after tab foregrounded');
            audioContextRef.current.resume().catch(err => {
              console.error('[AudioContext] Failed to resume after tab visible:', err);
            });
          }
        }
      };

      // Add event listener
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Cleanup on unmount or when recording stops
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        notification.destroy('tab-backgrounded');
      };
    }, [recording, session_id, patient_id]);

    // Listen to network status events from parent (Session.js via useNetworkStatus hook)
    // FIX (Stale Closure Audit — Bug #2-4): Use refs (recordingRef, pausedRef) instead of
    // closure-captured state values. The event handlers are registered once per effect cycle,
    // and between re-registrations the captured `recording`, `connectionState`, and `paused`
    // values can be stale. Refs always reflect the latest value.
    // Note: `connectionState` is a derived value from parent props — we check wsRef.current
    // readyState directly as a more reliable indicator of actual connection status.
    useEffect(() => {
      const handleNetworkRestored = () => {
        console.log('[ControlButtons] Network restored event received');

        if (!recordingRef.current) return;

        // Telemetry: network restored during recording (Gap #14)
        metrics.add(metrics.networkStatusEvent({
          session_id, patient_id, status: 'restored',
          ws_was_alive: wsRef.current?.readyState === WebSocket.OPEN,
        }));

        if (wsRef.current?.readyState === WebSocket.OPEN) {
          // FIX (Network Restore — WS Alive): The WebSocket survived the short outage
          // (TCP connection stayed alive). Restore UI state that was set by handleNetworkLost:
          // banner, pause state, notifications, and timer.
          console.log('[WebSocket] Network restored — existing WS still alive, restoring UI state');

          setWsState('connected');

          // Dismiss all connection-related notifications
          notification.destroy('connection-lost');
          notification.destroy('ws-error');
          notification.destroy('heartbeat-timeout');

          // Send a verification ping to confirm the connection is truly alive.
          // If the send throws, the WS is dead despite readyState — fall through to reconnect.
          try {
            wsRef.current.send(JSON.stringify({ type: "ping" }));
            console.log('[WebSocket] Verification ping sent after network restore');
          } catch (e) {
            console.warn('[WebSocket] Verification ping failed — WS is dead despite readyState, forcing reconnect');
            attemptReconnect();
            return;
          }

          // Auto-resume recording if it was paused due to disconnection
          if (pausedByDisconnectRef.current && pausedRef.current) {
            resumeRecording();
            pausedByDisconnectRef.current = false;
          }

          // Restart timer if it was stopped by handleNetworkLost
          startTicking();

          notification.success({
            key: 'connection-restored',
            message: "Connection Restored",
            description: "Your recording continues.",
            duration: 3,
          });
        } else {
          // WS is dead (long outage, TCP timed out) — create new connection
          notification.info({
            message: "Network Restored",
            description: "Attempting to reconnect...",
          });
          attemptReconnect();
        }
      };

      const handleNetworkLost = () => {
        console.log('[ControlButtons] Network lost event received');
        // Telemetry: network lost during recording (Gap #13)
        if (recordingRef.current) {
          metrics.add(metrics.networkStatusEvent({ session_id, patient_id, status: 'lost' }));
        }
        // FIX: Use recordingRef and pausedRef for fresh values instead of stale closure state
        if (recordingRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
          // Auto-pause recording when connection is lost
          if (!pausedRef.current) {
            pauseRecording();
            pausedByDisconnectRef.current = true; // Mark that pause was due to disconnect
          }
          stopTicking();
          setWsState('reconnecting');  // Update WebSocket state
          notification.warning({
            key: 'connection-lost',
            message: ERROR_MESSAGES.INTERNET_DISCONNECTED.message,
            description: ERROR_MESSAGES.INTERNET_DISCONNECTED.description,
            duration: 0,
          });
        }
      };

      // Listen to custom events dispatched by useNetworkStatus hook
      window.addEventListener('network:restored', handleNetworkRestored);
      window.addEventListener('network:lost', handleNetworkLost);

      return () => {
        window.removeEventListener('network:restored', handleNetworkRestored);
        window.removeEventListener('network:lost', handleNetworkLost);
      };
    }, [attemptReconnect, stopTicking, pauseRecording]);

    // Listen for recording stop event from navigation blocker
    // FIX (Stale Closure Audit — Bug #5): Use recordingRef.current instead of
    // closure-captured `recording` state to avoid stale value between re-registrations.
    useEffect(() => {
      const handleStopEvent = () => {
        if (recordingRef.current) {
          stopRecording();
        }
      };

      window.addEventListener('recording:stop', handleStopEvent);
      return () => window.removeEventListener('recording:stop', handleStopEvent);
    }, []);

    // Cleanup on unmount
    // FIX (Stale Closure Audit — Bug #14): Null-assign all timer refs after clearing
    // to prevent potential double-clear if other code paths run concurrently.
    useEffect(() => {
      return () => {
        // FIX (Audit Issue J): Set recordingRef to false FIRST to prevent any
        // in-flight reconnect setTimeout callbacks from creating orphan WebSockets
        // after the component has unmounted. The reconnect callback checks
        // `if (!recordingRef.current) return;` as its first guard.
        recordingRef.current = false;

        // FIX (Audit Issue F): Stop the timer interval to prevent setTimer()
        // calls on an unmounted component and avoid interval leaks.
        stopTicking();

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }

        // Clear heartbeat interval and timeout
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
        if (heartbeatTimeoutRef.current) {
          clearTimeout(heartbeatTimeoutRef.current);
          heartbeatTimeoutRef.current = null;
        }

        // Clear first-segment timeout
        if (firstSegmentTimeoutRef.current) {
          clearTimeout(firstSegmentTimeoutRef.current);
          firstSegmentTimeoutRef.current = null;
        }

        // Clear transcript inactivity timeout (FIX for Issue #5)
        if (transcriptInactivityTimeoutRef.current) {
          clearTimeout(transcriptInactivityTimeoutRef.current);
          transcriptInactivityTimeoutRef.current = null;
        }

        // Clear initial WebSocket connection timeout
        if (wsConnectionTimeoutRef.current) {
          clearTimeout(wsConnectionTimeoutRef.current);
          wsConnectionTimeoutRef.current = null;
        }

        // Release Web Lock and clear AudioContext watchdog on unmount
        if (wakeLockReleaseRef.current) { wakeLockReleaseRef.current(); wakeLockReleaseRef.current = null; }
        if (audioContextWatchdogRef.current) { clearInterval(audioContextWatchdogRef.current); audioContextWatchdogRef.current = null; }

        // EC-15 FIX: Cleanup audio resources on unmount to stop mic tracks
        cleanupAudio(true);

        // FIX (Stale Closure Audit — Bug #G): Set intentionalCloseRef before closing
        // the WebSocket on unmount. Without this, the close event (code 1000) would
        // be handled correctly by the `event.code === 1000` check in handleWebSocketClose,
        // but setting the ref provides defense-in-depth in case the close code changes
        // or the handler fires before the code check.
        // Clean up WebSocket if component unmounts while recording
        if (wsRef.current) {
          intentionalCloseRef.current = true;
          try {
            wsRef.current.close(1000, "Component unmounted");
          } catch { }
          wsRef.current = null;
        }
      };
    }, []);

    return (
      <Space size="small">
        <Upload
          beforeUpload={beforeUpload}
          showUploadList={false}
          accept=".json,.txt,.vtt,.vtt.txt"
        >
          {uploading ? (
            <>
              <Spin tip="Loading notes..." />
              <p className="mt-4 text-gray-500 text-sm font-medium">Loading notes...</p>
            </>

          ) : (
            <Button
              type="default"
              icon={<UploadOutlined style={{ color: "#1677ff" }} />}
              style={{
                ...buttonStyle,
                borderColor: "#1677ff",
                color: "white",
              }}
              disabled={recording || paused || uploading || isDisabledByEhr || !isOnline || isGeneratingAiNotes}
              title={
                isGeneratingAiNotes
                  ? "Cannot upload while AI notes are being generated"
                  : !isOnline
                  ? "Cannot upload without internet connection"
                  : isEhrSubmitted
                    ? "Note has been submitted to EHR"
                    : "Upload Transcript"
              }
            />
          )}
        </Upload>
        <Button

          type="primary"
          icon={<PlayCircleOutlined />}
          style={{
            ...buttonStyle,
            backgroundColor: recording ? "#e5e7eb" : "#1677ff",
            borderColor: recording ? "#e5e7eb" : "#1677ff",
          }}
          disabled={recording || uploading || isDisabledByEhr || !isOnline || isGeneratingAiNotes}
          onClick={handleStartClick}
          title={
            isGeneratingAiNotes
              ? "Cannot start recording while AI notes are being generated"
              : !isOnline
              ? "Cannot start recording without internet connection"
              : isEhrSubmitted
                ? "Note has been submitted to EHR"
                : "Start Recording"
          }
        />
        {recording && !paused && (
          <Button
            type="default"
            icon={<PauseCircleOutlined />}
            style={{
              ...buttonStyle,
              backgroundColor: "#6b7280",
              borderColor: "#6b7280",
              color: "white",
            }}
            onClick={pauseRecording}
            disabled={!recording || paused}
            title="Pause Recording"
          />
        )}
        {recording && paused && (
          <Button
            type="default"
            icon={<ReloadOutlined />}
            style={{
              ...buttonStyle,
              backgroundColor: "#1677ff",
              borderColor: "#1677ff",
              color: "white",
            }}
            onClick={resumeRecording}
            disabled={!recording || !paused || !isOnline}
            title={
              !isOnline
                ? "Cannot resume without internet connection"
                : "Resume Recording"
            }
          />
        )}
        <Button
          onClick={stopRecording}
          disabled={!recording}
          danger
          icon={<StopOutlined />}
          style={{
            ...buttonStyle,
            backgroundColor: !recording ? "#e5e7eb" : "#ef4444",
            borderColor: !recording ? "#e5e7eb" : "#ef4444",
          }}
          title={
            connectionState === 'reconnecting'
              ? "Stop Recording (reconnecting...)"
              : "Stop Recording"
          }
        />
      </Space>
    );
  }
);

export default SessionRightPanel;
